/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListTriggerInstancesRequest,
  ListTriggerInstancesSecurity,
} from "../models/operations/listtriggerinstances.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildTriggersQuery,
  prefetchTriggers,
  queryKeyTriggers,
  TriggersQueryData,
} from "./triggers.core.js";
export {
  buildTriggersQuery,
  prefetchTriggers,
  queryKeyTriggers,
  type TriggersQueryData,
};

export type TriggersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listTriggerInstances triggers
 *
 * @remarks
 * List trigger instances for the current project.
 */
export function useTriggers(
  request?: ListTriggerInstancesRequest | undefined,
  security?: ListTriggerInstancesSecurity | undefined,
  options?: QueryHookOptions<TriggersQueryData, TriggersQueryError>,
): UseQueryResult<TriggersQueryData, TriggersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildTriggersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listTriggerInstances triggers
 *
 * @remarks
 * List trigger instances for the current project.
 */
export function useTriggersSuspense(
  request?: ListTriggerInstancesRequest | undefined,
  security?: ListTriggerInstancesSecurity | undefined,
  options?: SuspenseQueryHookOptions<TriggersQueryData, TriggersQueryError>,
): UseSuspenseQueryResult<TriggersQueryData, TriggersQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildTriggersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setTriggersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: TriggersQueryData,
): TriggersQueryData | undefined {
  const key = queryKeyTriggers(...queryKeyBase);

  return client.setQueryData<TriggersQueryData>(key, data);
}

export function invalidateTriggers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "list", ...queryKeyBase],
  });
}

export function invalidateAllTriggers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "list"],
  });
}
